<template>
  <BContainer
    class="mb-4 px-0"
  >
    <BRow
      class="pb-2"
    >

      <BCol
        order="1"
        cols="12"
        md="6"
      >
        <ValidationObserver
          ref="formDocument"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="document_type_id"
              name="document type"
              rules="required"
            >
              <BFormGroup
                label="Document Type"
                label-for="fDocumentType"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.documentType"
                  input-id="fDocumentType"
                  label="document_type_name"
                  append-to-body
                  placeholder="select document type here"
                  :options="filteredDocuments"
                  :disabled="fetching.documentTypes || !activity.activity_type"
                  :loading="fetching.documentTypes"
                  :class="{ 'is-invalid': errors.length > 0 }"
                >
                  <template #selected-option="{ document_type_name }">
                    <div
                      class="w-225px w-lg-325px text-truncate"
                      v-text="document_type_name"
                    />
                  </template>
                  <template #no-options="">
                    no available document type
                  </template>
                </VSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </form>
        </ValidationObserver>
      </BCol>

      <BCol
        order-md="2"
        order="3"
      >
        <BFormGroup
          class="mb-2"
        >
          <label
            for="actionButtons"
            class="d-none d-md-block"
          >
            &nbsp;
          </label>
          <BButton
            variant="primary"
            :disabled="state.busy || !selected.documentType"
            @click="validateDocument"
          >
            Add Document Type
          </BButton>
        </BFormGroup>
      </BCol>

      <BCol
        order-md="3"
        order="2"
        cols="12"
        md="6"
      >
        <BFormGroup>
          <BFormCheckbox
            id="fDocumentRequired"
            v-model="formDocument.required"
            switch
            value="1"
            name="required"
            unchecked-value="0"
            :disabled="state.busy"
          >
            Required to Upload Document?
          </BFormCheckbox>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        order="4"
      >
        <BFormGroup
          label="Search"
          label-for="filterDocumentTypeSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterDocumentTypeSearch"
            v-model="tableDocuments.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <BTable
      ref="tableDocuments"
      small
      hover
      bordered
      responsive
      show-empty
      :items="tableProvider"
      :filter="tableDocuments.filter"
      :stacked="tableDocuments.stacked"
      :fields="tableDocuments.fields"
    >
      <template #cell(index)="{ index }">
        {{ index + 1 }}
      </template>
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="danger"
            @click.prevent="deleteDocument(item)"
          >
            Delete
          </BButton>
        </div>
      </template>
    </BTable>
  </BContainer>
</template>

<script>
import QUERY from 'lodash'
import { fuseFilter } from '@/plugins/fuse'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MDocumentTypes from '@/mixins/maintenances/document-types'

export default {
  name: 'VAdminActivityDocumentTypes',

  mixins: [
    MxMisc,
    MxFormatter,
    MDocumentTypes
  ],

  middleware: [
    'auth',
    'admin'
  ],

  props: {
    activity: {
      type: Object,
      required: true,
      default: () => ({})
    },
    documentTypes: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  emits: ['updated'],

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formDocument: {
        id: '',
        document_type_code: '',
        document_type_name: '',
        upload_limit: '',
        required: 1
      },
      tableDocuments: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: this.documentTypes,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 5, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'document_type_code', class: 'min-w-175px' },
          { mobile: 2, key: 'document_type_name', class: 'min-w-175px' },
          { mobile: 3, key: 'upload_limit', class: 'min-w-75px' },
          { mobile: 4, key: 'required', formatter: this.yesOrNo }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    filteredDocuments () {
      return this.options.documentTypes.filter(
        ({ id }) => !Array.from(this.tableDocuments.rows).some(
          documentType => Number(documentType.id) === Number(id)
        )
      )
    }
  },

  watch: {
    'selected.documentType' (documentType) {
      this.formDocument = Object.assign(this.formDocument, {
        id: documentType?.id || '',
        document_type_name: documentType?.document_type_name || '',
        document_type_code: documentType?.document_type_code || '',
        upload_limit: documentType?.upload_limit || ''
      })
    },

    'activity.activity_type' (activityType) {
      if (activityType) {
        this.selected.documentCategory = activityType

        this.getDocumentTypes(this)
      } else {
        this.options.documentTypes = []
      }
    },

    'activity.role_id' (role) {
      this.selected.role = role

      this.getDocumentTypes(this)
    }
  },

  mounted () {
    this.selected.documentCategory = this.activity.activity_type
    this.selected.role = this.activity.role_id
  },

  methods: {

    async tableProvider ({ filter }) {
      return fuseFilter(filter.search, this.tableDocuments.rows, {
        keys: ['document_type_name']
      })
    },

    clearDocument () {
      this.state.editing = false

      this.selected.documentType = ''

      this.formDocument = {
        id: '',
        document_type_code: '',
        document_type_name: '',
        upload_limit: '',
        required: 1
      }

      this.$refs.formDocument.reset()
    },

    editDocument (documentType) {
      this.state.editing = true

      this.selected.documentType = QUERY.find(this.options.documentTypes, {
        id: documentType.id
      })
    },

    validateDocument () {
      this.$refs.formDocument.validate().then(
        async validDocument => {
          if (!validDocument) {
            return this.swalInvalid()
          }

          const documentTypeExists = QUERY.find(this.tableDocuments.rows, {
            id: this.formDocument.id
          })

          if (!documentTypeExists) {
            this.tableDocuments.rows.push(this.formDocument)
          }

          this.clearDocument()
          this.refetch()
        }
      )
    },

    deleteDocument (documentType) {
      this.tableDocuments.rows = QUERY.filter(this.tableDocuments.rows,
        ({ id }) => Number(id) !== Number(documentType.id)
      )

      this.refetch()
    },

    refetch () {
      this.$emit('updated', this.tableDocuments.rows)

      this.$refs.tableDocuments.refresh()
    },

    setErrors (errors) {
      this.$refs.formDocument.setErrors(errors)
    }
  }
}
</script>
